// All user-facing English text. Portuguese (pt.ts) will mirror these keys.
import type { ClueLine } from "../game/attrs";
/** Numbers as players read them: 1,000 not 1000. */
export const n = (x: number) => x.toLocaleString("en-GB");
/** A wait in words: "7 h 12 min", "12 min", "3 h", "2 days". */
const span = (ms: number): string => {
  const mins = Math.max(1, Math.ceil(ms / 60_000));
  if (mins >= 48 * 60) return `${Math.round(mins / (24 * 60))} days`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h ? `${h} h` : ""}${h && m ? " " : ""}${m ? `${m} min` : ""}`;
};

export const en = {
  productName: "WhoSteps",
  motto: "Map it happen.",
  tagline: "A daily map puzzle. Follow the footsteps of famous people, stories and events.",
  levels: {
    easy: { label: "Family", sub: "Kids 8+ and parents" },
    medium: { label: "Classic", sub: "For grown-ups" },
    // Merged into Classic (2026-09-29); kept so old saved data and links still have a label.
    hard: { label: "Classic", sub: "For grown-ups" },
  },
  /** Theme tag labels (DECISIONS "Theme tags"); Family wording where it differs. Never shown during a daily round. */
  themes: {
    classic: { history: "History", journeys: "Explorers & journeys", sport: "Sport", screen: "Film & TV", books: "Literature", music: "Music", science: "Science & invention", art: "Art", war: "War & conflict", leaders: "Leaders & politics", myths: "Myths & legends", places: "Places & landmarks", nature: "Animals & nature" },
    family: { history: "History", journeys: "Adventure", sport: "Sport", screen: "Films & stories", books: "Books", music: "Music", science: "Science", art: "Art", war: "", leaders: "Kings, queens & leaders", myths: "Myths & legends", places: "Famous places", nature: "Animals & nature" },
  },
  roundTitles: {
    pin: "Pin it",
    who: "Whose steps?",
    next: "Next stop",
  },
  ui: {
    difficulty: "Difficulty",
    puzzleNumber: "Puzzle number",
    /** Playtest day picker, folded: "Puzzle #3 of 28". */
    dayPicker: (num: number, count: number) => `Puzzle #${num} of ${count}`,
    dayPickerNone: (count: number) => `Pick a puzzle (${count})`,
    map: "Map",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    resetView: "Reset view",
    totalIdle: (max: number) => `3 rounds · ${n(max)} pts`,
    total: (score: number, max: number) => `${n(score)} / ${n(max)}`,
    roundOf: (r: number, level: string) => `Round ${r} of 3 · ${level}`,
    nextRound: "Next round →",
    seeScore: "See your score",
    /** "Whose steps?" verdict word by the place it was named at; any place is a win. */
    whoVerdict: (place: number) => ["Not this time", "Brilliant", "Excellent", "Great", "Well done", "Got it"][place] ?? "Got it",
    verdict: (score: number) => (score >= 900 ? "Excellent" : score >= 600 ? "Good" : score >= 300 ? "Close-ish" : score > 0 ? "A few points" : "Not this time"),
    points: (score: number) => `+${n(score)} ${score === 1 ? "pt" : "pts"}`,
    verdictAnnounce: (word: string, reveal: string, score: number) => `${word ? `${word}. ` : ""}${reveal}. ${n(score)} ${score === 1 ? "point" : "points"}.`,
    howTo: "How to play",
    didYouKnow: "Did you know?",
    days: "Puzzle days",
    comingSoon: "The first daily puzzle is on its way. Check back soon.",
    loading: "Loading today's puzzle…",
    loadingDay: "Loading that day…",
    loadFailed: "Today's puzzle didn't load. Check your connection and try again.",
    retry: "Try again",
    draftsBanner: "Playtest build: every day, including future dates and unverified drafts. Progress here is separate from the real game.",
    announceIntro: "How to play WhoSteps",
    tapPins: "Select a pin on the map to see what happened there.",
    privacy: "Privacy",
    mapKeys: "Map. Arrow keys move the map, plus and minus zoom.",
    mapKeysPick: "Map. Arrow keys move the map, plus and minus zoom, Enter drops a pin at the centre.",
  },
  pin: {
    /** One-time note by the first pin (Classic), numbers from scoring.ts. */
    zoomNudge: (km: number, pts: number) => `Zoom in to be precise — ${n(pts)} points means within ${n(km)} km.`,
    lock: "Lock in",
    moveHint: "Tap or click the map again to move your pin.",
    yourPin: "Your pin",
    /** Under 1 km from the true spot: no distance line, just this. */
    spotOn: "Spot on",
    spotOnVerdict: (word: string) => `${word} — spot on`,
    /** Hints (DECISIONS 2026-10-03): each lowers the most the round can score. */
    hintAsk: (cap: number) => `Need a hint? Most you can score: ${n(cap)}`,
    hintLabel: (i: number) => `Hint ${i}`,
    hintsList: "Hints",
    hintAnnounce: (i: number, text: string, cap: number) => `Hint ${i}: ${text} Most you can score is now ${n(cap)}.`,
    worthUpTo: (pts: number) => `Worth up to ${n(pts)}`,
    hintsUsed: (used: number, of: number) => (used ? `${used} of ${of} ${of === 1 ? "hint" : "hints"}` : of === 1 ? "1 hint available" : `${of} hints available`),
    /** Summary row suffix: "16 / 20 · 1 hint". */
    hintsRow: (used: number) => ` · ${used} ${used === 1 ? "hint" : "hints"}`,
    /** After the round: the person's three steps through the true spot. */
    trail: "Their steps",
    here: "Here",
    /** Label on a distance ring around the answer (points for landing inside it). */
    ring: (pts: number) => `${pts} pts`,
    away: (word: string, d: string) => `${word} — ${d} off`,
  },
  next: {
    verdict: (word: string, name: string, d: string) => `${word} — ${name}, ${d} from your pin`,
    verdictSpotOn: (word: string, name: string) => `${word} — ${name}, spot on`,
    answer: "Next stop",
    route: "The journey",
  },
  who: {
    introPerson: "Someone, real or fictional, left these footsteps. Places appear one at a time. Name them as early as you can.",
    introThing: "Someone, or something, left these footsteps. Places appear one at a time. Name them as early as you can.",
    placeOf: (shown: number) => `Place ${shown} of 5`,
    worth: (pts: number) => `Worth ${n(pts)} pts`,
    placeholder: "Type a name",
    ariaGuess: "Your guess",
    suggestions: "Suggestions",
    guess: "Guess",
    hardHint: "Hard mode: years stay hidden until the end.",
    skipped: "skipped",
    another: (next: number, pts: number) => `Show place ${next} · worth ${n(pts)}`,
    giveUp: "Give up",
    not: (g: string) => `Not ${g}. Here's another place.`,
    anotherPlace: "Here's another place.",
    found: (shown: number, answer: string) => `Found on place ${shown}: ${answer}`,
    itWas: (answer: string) => `It was ${answer}`,
    cluesLabel: "Places so far",
    placeAnnounce: (i: number, name: string, when: string | null) => `Place ${i}: ${name}${when ? `, ${when}` : ""}`,
    notAnnounce: (g: string) => `Not ${g}.`,
    wrongGuess: (g: string) => `Wrong guess: ${g}`,
    skippedLabel: "Skipped a place",
    /**
     * The clue line after a wrong guess (DECISIONS 2026-10-03), e.g. "Magellan — ✗ century (answer is later ↑) · ✓ Explorers
     * & journeys · ✗ from Europe". `field` is the guess's field label at this level. Family: plain words.
     */
    clueLine: (guess: string, c: ClueLine, field: string | null) =>
      `${guess} — ${c.century == null ? "— century" : c.century === "same" ? "✓ century" : `✗ century (answer is ${c.century === "later" ? "later ↑" : "earlier ↓"})`} · ${c.field && field ? `${c.field.same ? "✓" : "✗"} ${field}` : "— field"} · ${c.from ? `${c.from.same ? "✓" : "✗"} from ${c.from.from}` : "— from"}`,
    clueLineFamily: (guess: string, c: ClueLine) =>
      `${guess} — ${c.century == null ? "—" : c.century === "same" ? "Same time" : "Different time"} · ${c.field ? (c.field.same ? "Same kind" : "Different kind") : "—"} · ${c.from ? (c.from.same ? "Same place" : "Different place") : "—"}`,
    /** The same line for screen readers, without symbols. */
    clueLineSpoken: (guess: string, c: ClueLine, field: string | null) =>
      `${guess}: ${c.century == null ? "century unknown" : c.century === "same" ? "same century" : `different century, the answer is ${c.century}`}; ${c.field && field ? `${c.field.same ? "same field" : "different field"}, ${field}` : "field unknown"}; ${c.from ? `${c.from.same ? "same continent" : "different continent"}, ${c.from.from}` : "continent unknown"}.`,
    clueLinesLabel: "How your guesses compare",
    /** Typed text that isn't a name on the list: not a guess, no place shown, no points lost. */
    pickFromList: "Pick a name from the list. That one isn't on it, so it doesn't count as a guess.",
  },
  intro: {
    title: "How to play",
    what: "WhoSteps is a daily map puzzle about the places where famous people, stories and events left their mark.",
    // Numbers come from src/game/scoring.ts (the ladder lives there and nowhere else).
    rounds: (max: number) => `3 rounds a day. Pin it: read a clue and drop a pin (up to ${n(max)} points). Next stop: a journey is drawn part-way, drop a pin where it went next (up to ${n(max)} points). Whose steps?: guess who left this footprint on the map (up to ${n(max)} points).`,
    /** The one scoring rule (DECISIONS 2026-10-03), written once. `rings` = [km, points] pairs; `caps` = hint caps; `who` = the ladder. */
    rule: (rings: readonly (readonly [number, number])[], caps: readonly number[], who: readonly number[]) =>
      `One rule for points: distance shows how close you were, and every extra step or hint you need moves you down the ladder. Pin it and Next stop: ${rings.map(([km, pts], i) => `${n(pts)}${i ? "" : " points"} within ${n(km)} km`).join(", ")}. Each Pin it hint lowers the most you can score: ${caps.slice(1).map(n).join(", then ")}. In “Whose steps?” every place shown or wrong guess moves you down: ${who.map(n).join(" → ")}.`,
    /** The one signature (DECISIONS 2026-10-03), written once. */
    signature: "Every round ends with the person's route on the map: three of their steps in Pin it, the whole journey in Next stop, the whole life in “Whose steps?”.",
    daily: "A new puzzle every morning at 6am, your time.",
    who: "The last round is always “Whose steps?”. Places appear one by one, oldest first: name the person as early as you can. A wrong guess shows the next place.",
    levels: "Family is its own puzzle, made for kids and parents, with helpers. Classic is the puzzle for grown-ups.",
    /** First visit (DECISIONS 2026-10-03 "A shorter first visit"): three lines over the map. */
    quickTitle: "How it works",
    quick: ["Read the clue", "Drop one pin", "Closer is more points"],
    gotIt: "Got it",
    close: "Got it, let's play",
    closeAgain: "Close",
    privacy: "No accounts, no ads, no tracking cookies.",
  },
  share: {
    message: (day: number, level: string, total: number, max: number) => `I got ${n(total)}/${n(max)} on WhoSteps #${day} (${level}). Can you beat me?`,
    /** Share text v6: a day played in the Vault says so. */
    messageVault: (day: number, level: string, total: number, max: number) => `I got ${n(total)}/${n(max)} on WhoSteps #${day} (${level}, from the Vault). Can you beat me?`,
  },
  summary: {
    kicker: (num: number, level: string) => `Puzzle #${num} · ${level} · complete`,
    /** The "Whose steps?" row names the answer, for anyone opening the finished day later. */
    whoRow: (title: string, answer: string) => `${title} · ${answer}`,
    copied: "Result copied. Paste it anywhere.",
    shareTo: { whatsapp: "WhatsApp", telegram: "Telegram" },
    copyLink: "Copy link",
    legend: "The day on the map",
    legendPin: "Pin it",
    legendNext: "Next stop",
    legendWho: "Whose steps?",
    copiedShort: "Copied",
    share: "Share result",
    next: "Next →",
    copyFallback: "Selected. Copy it with your keyboard or the share menu.",
    shareNote: "The link lets friends play today's puzzle and compare scores afterwards.",
    replay: "Reset this day (playtest)",
    storageNote: "Your progress is kept in this browser only.",
    backup: "Copy backup link",
    backupCopied: "Backup link copied. Keep it somewhere safe (notes, an email to yourself). Opening it restores your streak and places on any device.",
    backupFailed: "Couldn't make the backup link in this browser.",
  },
  /** The share image (DECISIONS 2026-10-03 "A spoiler-free share card"): score only, never the day's content. */
  card: {
    puzzle: (num: number, level: string, vault: boolean) => `Puzzle #${num} · ${level}${vault ? " (from the Vault)" : ""}`,
    score: (got: number, max: number) => `${n(got)} / ${n(max)}`,
    pin: "Pin it",
    next: "Next stop",
    who: "Whose steps?",
    named: (place: number) => (place ? `Named at place ${place}` : "Not named"),
    cta: "Can you beat me?",
    alt: "My WhoSteps score card",
    shareImage: "Share image",
    downloadImage: "Download image",
    making: "Making the image…",
    failed: "Couldn't make the image in this browser.",
  },
  streak: {
    label: "Played streak",
    days: (count: number) => `${count} ${count === 1 ? "day" : "days"}`,
    best: (count: number) => `Best ${count}`,
    bank: (count: number) => (count === 1 ? "1 rest day saved" : `${count} rest days saved`),
  },
  challenge: {
    pending: (num: number) => `A friend challenged you on #${num}. Their score appears when you finish.`,
    otherDay: (num: number, level: string) => `Your friend's challenge was for #${num} (${level}), so it can't be compared with this puzzle.`,
    them: "Your friend",
    you: "You",
    line: (score: number, place: number) => `${n(score)} · ${place ? `named at place ${place}` : "not named"}`,
    win: "You win this one.",
    lose: "Your friend wins this one.",
    tie: "A tie.",
    /** Equal totals, settled by who named the person earlier ("not named" counts as place 6). */
    tieBreak: "Same score, so the earlier name wins.",
    /** A challenge link from before the restart (DECISIONS 2026-10-03 "Restart"). */
    old: "That challenge link is from before WhoSteps started again at #1, so it can't be compared. Here's today's puzzle.",
  },
  /** The Vault: past days, after today's puzzle (DECISIONS "The Vault"). */
  vault: {
    button: "The Vault",
    locked: "Finish today's puzzle to open the Vault.",
    kicker: (level: string) => `The Vault · ${level}`,
    title: "Past days",
    intro: "Every earlier puzzle. They add places to your map, but not days to your streak.",
    unplayedOnly: "Unplayed only",
    /** "Fri 10 Oct" from "2026-10-10". */
    date: (iso: string) => {
      const d = new Date(`${iso}T12:00:00`);
      return `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()]} ${d.getDate()} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()]}`;
    },
    unplayed: "Unplayed",
    partial: (played: number) => `${played} of 3 rounds`,
    score: (score: number, max: number) => `${n(score)}/${n(max)}`,
    allPlayed: "You've played every past day. A new one joins the Vault each day.",
    today: "Today's puzzle",
    todayArrow: "Today's puzzle →",
    back: "Back to the Vault",
    nextUnplayed: "Next unplayed →",
    playAgain: "Play again (practice)",
    roundKicker: (r: number, num: number) => `From the Vault · #${num} · Round ${r} of 3`,
    practiceRound: (r: number) => `Practice — not scored · Round ${r} of 3`,
    summaryKicker: (num: number, level: string) => `Puzzle #${num} · ${level} · from the Vault`,
    practiceKicker: (num: number) => `Puzzle #${num} · Practice — not scored`,
    practiceNote: (score: number, max: number) => `Practice — not scored. Your score for this day stays ${n(score)}/${n(max)}.`,
    loadFailed: "That day didn't load. Check your connection and try again.",
  },
  /** What the finished day offers next: no separate home screen (Egor, 29 Sep). */
  hub: {
    label: "What's next",
    nextIn: (ms: number) => `Next puzzle in ${span(ms)}`,
    firstIn: (ms: number) => `The first puzzle arrives in ${span(ms)}.`,
    ready: "A new puzzle is ready.",
    playNew: "Play it →",
    alsoPlay: (level: string) => `Play ${level} too →`,
    pastDays: "Past days →",
  },
  restore: {
    done: "Progress restored from your backup link.",
    failed: "That backup link didn't work. Check it was copied in full.",
    old: "That backup link is from before WhoSteps started again at #1, so there's nothing in it for the new puzzles.",
  },
} as const;

export type Strings = typeof en;
